// src/components/tiptap/extensions/TimerToolNode.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../../../common/SafeIcon.jsx";
import ConfirmModal from "../../common/ConfirmModal.jsx";

const { FiClock, FiPlay, FiPause, FiRotateCcw, FiFlag, FiTrash2, FiCheckCircle } = FiIcons;

const cls = (...xs) => xs.filter(Boolean).join(" ");

/* helper to allow form controls to keep default behavior */
const isFormControl = (el) =>
  el && (
    el.tagName === "SELECT" ||
    el.tagName === "INPUT" ||
    el.tagName === "TEXTAREA" ||
    el.isContentEditable
  );

function pad2(n) {
  const x = Math.max(0, Math.floor(Number(n) || 0));
  return x < 10 ? `0${x}` : String(x);
}

function formatClock(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 ? `${h}:${pad2(m)}:${pad2(s)}` : `${m}:${pad2(s)}`;
}

// NOTE: This tool must NOT earn stickers or trigger sticker selection.
// We only beep + show an on-screen message when countdown completes.

function playBeeps(times = 3) {
  try {
    // Reuse a shared AudioContext where possible (keeps it snappy)
    const g = typeof window !== "undefined" ? window : null;
    const AC = g?.AudioContext || g?.webkitAudioContext;
    if (!AC) return;

    // @ts-ignore - store on window for reuse
    const ctx = (g.__mmsTimerAudioCtx && g.__mmsTimerAudioCtx.state !== "closed")
      ? g.__mmsTimerAudioCtx
      : (g.__mmsTimerAudioCtx = new AC());

    // Some browsers suspend contexts until user gesture; start is always a gesture,
    // and countdown completion happens after, so resume is usually allowed.
    if (ctx.state === "suspended") ctx.resume?.().catch(() => {});

    const now = ctx.currentTime;
    const beepDur = 0.12;     // seconds
    const gap = 0.16;         // seconds between beeps
    const freq = 880;         // Hz (pleasant, noticeable)

    for (let i = 0; i < Math.max(1, times); i++) {
      const t0 = now + i * (beepDur + gap);
      const t1 = t0 + beepDur;

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, t0);

      // Soft envelope to avoid clicks
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.25, t0 + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, t1);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(t0);
      osc.stop(t1 + 0.02);
      osc.onended = () => {
        try { osc.disconnect(); } catch {}
        try { gain.disconnect(); } catch {}
      };
    }
  } catch {
    // ignore
  }
}

function TimerToolBlock({ node, updateAttributes, editor, deleteNode }) {
  const attrs = node.attrs || {};
  const ctx = editor?.storage?.mms ?? {};
  const canDelete = !!ctx?.userId && !!editor?.isEditable; // teachers only

  // Seed noteBlockId once (playbook pattern)
  useEffect(() => {
    if (!attrs.noteBlockId) {
      updateAttributes({ noteBlockId: crypto.randomUUID?.() ?? String(Date.now()) });
    }
  }, []);

  const [mode, setMode] = useState(attrs.mode ?? "timer"); // 'timer' | 'countdown'
  const [running, setRunning] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [laps, setLaps] = useState([]);

  // Countdown config (not persisted)
  const [mm, setMm] = useState(3);
  const [ss, setSs] = useState(0);
  const [durationMs, setDurationMs] = useState(3 * 60 * 1000);

  const [done, setDone] = useState(false);
  const doneRef = useRef(false);

  const rafRef = useRef(null);
  const startRef = useRef(null);  // performance.now at start/resume
  const baseRef = useRef(0);      // ms accumulated before start/resume
  const beepedRef = useRef(false);

  // Clean up RAF on unmount
  useEffect(() => {
    return () => {
      try {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      } catch {}
      rafRef.current = null;
    };
  }, []);

  // Keep attrs.mode in sync
  useEffect(() => {
    if (attrs.mode !== mode) updateAttributes({ mode });
  }, [mode]);

  const remainingMs = useMemo(() => {
    if (mode !== "countdown") return 0;
    return Math.max(0, durationMs - elapsedMs);
  }, [mode, durationMs, elapsedMs]);

  const displayMs = mode === "countdown" ? remainingMs : elapsedMs;

  const progress = useMemo(() => {
    if (mode !== "countdown") return 0;
    if (durationMs <= 0) return 0;
    const p = 1 - (remainingMs / durationMs);
    return Math.max(0, Math.min(1, p));
  }, [mode, durationMs, remainingMs]);

  const stopLoop = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  };

  const tick = () => {
    const now = performance.now();
    const startedAt = startRef.current ?? now;
    const base = baseRef.current ?? 0;
    const nextElapsed = base + (now - startedAt);

    if (mode === "countdown") {
      const nextRemaining = Math.max(0, durationMs - nextElapsed);
      setElapsedMs(durationMs - nextRemaining);

      if (nextRemaining <= 0.5) {
        // Finish
        stopLoop();
        setRunning(false);
        baseRef.current = durationMs;
        startRef.current = null;

        if (!doneRef.current) {
          doneRef.current = true;
          setDone(true);
          // Beep a few times + show message, but DO NOT trigger stickers.
          if (!beepedRef.current) {
            beepedRef.current = true;
            playBeeps(3);
          }
        }
        return;
      }
    } else {
      setElapsedMs(nextElapsed);
    }

    rafRef.current = requestAnimationFrame(tick);
  };

  const start = () => {
    if (running) return;
    setDone(false);
    doneRef.current = false;
    beepedRef.current = false;
    startRef.current = performance.now();
    baseRef.current = elapsedMs;
    setRunning(true);
    rafRef.current = requestAnimationFrame(tick);
  };

  const pause = () => {
    if (!running) return;
    stopLoop();
    const now = performance.now();
    const startedAt = startRef.current ?? now;
    baseRef.current = (baseRef.current ?? 0) + (now - startedAt);
    startRef.current = null;
    setElapsedMs(baseRef.current);
    setRunning(false);
  };

  const reset = () => {
    stopLoop();
    setRunning(false);
    startRef.current = null;
    baseRef.current = 0;
    setElapsedMs(0);
    setLaps([]);
    setDone(false);
    doneRef.current = false;
    beepedRef.current = false;
  };

  const addLap = () => {
    if (mode !== "timer") return;
    if (!running && elapsedMs <= 0) return;
    setLaps((prev) => [{ t: elapsedMs, id: crypto.randomUUID?.() ?? String(Date.now()) }, ...prev].slice(0, 20));
  };

  const applyCountdown = (nextMinutes, nextSeconds) => {
    const m = Math.max(0, Math.min(999, Number(nextMinutes) || 0));
    const s = Math.max(0, Math.min(59, Number(nextSeconds) || 0));
    setMm(m);
    setSs(s);
    const next = (m * 60 + s) * 1000;
    setDurationMs(next);
    // Reset elapsed for a clean start
    stopLoop();
    setRunning(false);
    startRef.current = null;
    baseRef.current = 0;
    setElapsedMs(0);
    setDone(false);
    doneRef.current = false;
    beepedRef.current = false;
  };

  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <NodeViewWrapper
      as="div"
      id={attrs.noteBlockId ? `tool-${attrs.noteBlockId}` : undefined}
      contentEditable={false}
      role="group"
      tabIndex={-1}
      className="relative group w-full rounded-2xl bg-slate-50 shadow-sm p-4 border border-slate-200"
      onMouseDownCapture={(e) => {
        if (!isFormControl(e.target)) e.preventDefault();
        e.stopPropagation();
      }}
      onTouchStartCapture={(e) => {
        if (!isFormControl(e.target)) e.preventDefault();
        e.stopPropagation();
      }}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-4 min-w-0">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center">
            <SafeIcon icon={FiClock} className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <div className="text-lg font-bold text-slate-800">Timer</div>
            <div className="text-sm text-slate-500">Count up, or run a focused countdown.</div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 flex-wrap sm:flex-nowrap min-w-0">
          {/* Mode toggle */}
          <div className="inline-flex rounded-xl border border-slate-200 bg-white/70 p-1">
            <button
              type="button"
              className={cls(
                "px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors",
                mode === "timer" ? "bg-orange-500 text-white shadow" : "text-slate-600 hover:bg-slate-100"
              )}
              onClick={() => {
                if (running) pause();
                setMode("timer");
                reset();
              }}
              title="Count up"
            >
              Timer
            </button>
            <button
              type="button"
              className={cls(
                "px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors",
                mode === "countdown" ? "bg-orange-500 text-white shadow" : "text-slate-600 hover:bg-slate-100"
              )}
              onClick={() => {
                if (running) pause();
                setMode("countdown");
                reset();
                applyCountdown(mm, ss);
              }}
              title="Countdown"
            >
              Countdown
            </button>
          </div>

          {canDelete && (
            <button
              type="button"
              className="p-2 rounded-full text-slate-500 hover:bg-red-100 hover:text-red-600 transition-colors opacity-50 group-hover:opacity-100"
              onClick={() => setConfirmOpen(true)}
              title="Delete Tool"
            >
              <SafeIcon icon={FiTrash2} className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Display */}
        <div className="lg:col-span-2 bg-white/80 rounded-2xl border border-slate-200 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs font-semibold text-slate-500">
                {mode === "countdown" ? "Remaining" : "Elapsed"}
              </div>
              <div className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-800 mt-1">
                {formatClock(displayMs)}
              </div>

              {mode === "countdown" && (
                <div className="text-xs text-slate-500 mt-2">
                  Total: {formatClock(durationMs)}
                </div>
              )}
            </div>

            {/* Progress ring (countdown only) */}
            {mode === "countdown" && (
              <div
                className="shrink-0 w-20 h-20 rounded-full border border-slate-200 bg-white flex items-center justify-center"
                style={{
                  background: `conic-gradient(#f97316 ${progress * 360}deg, rgba(148,163,184,0.25) 0deg)`,
                }}
                aria-hidden="true"
              >
                <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center border border-slate-200">
                  <div className="text-xs font-bold text-slate-700">
                    {Math.round(progress * 100)}%
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <button
              type="button"
              className={cls(
                "px-4 py-2.5 rounded-xl text-base font-semibold text-white shadow-lg transition-all transform flex items-center justify-center gap-2",
                "bg-orange-500 hover:bg-orange-600 active:scale-[0.98]"
              )}
              onClick={() => (running ? pause() : start())}
              title={running ? "Pause" : "Start"}
            >
              <SafeIcon icon={running ? FiPause : FiPlay} />
              {running ? "Pause" : "Start"}
            </button>

            <button
              type="button"
              className="px-4 py-2.5 rounded-xl text-base font-semibold border bg-white/80 border-slate-300 hover:bg-slate-100 transition-colors flex items-center justify-center gap-2"
              onClick={reset}
              title="Reset"
            >
              <SafeIcon icon={FiRotateCcw} />
              Reset
            </button>

            {mode === "timer" && (
              <button
                type="button"
                className={cls(
                  "px-4 py-2.5 rounded-xl text-base font-semibold border bg-white/80 border-slate-300 transition-colors flex items-center justify-center gap-2",
                  (!running && elapsedMs <= 0) ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-100"
                )}
                onClick={addLap}
                disabled={!running && elapsedMs <= 0}
                title="Lap"
              >
                <SafeIcon icon={FiFlag} />
                Lap
              </button>
            )}
          </div>

          {/* Done banner */}
          {done && (
            <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-3 flex items-start gap-2">
              <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center text-green-700">
                <SafeIcon icon={FiCheckCircle} />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-green-700">Time’s up!</div>
                <div className="text-sm text-green-700/80">Nice work staying focused.</div>
              </div>
            </div>
          )}
        </div>

        {/* Right panel */}
        <div className="bg-white/80 rounded-2xl border border-slate-200 p-4">
          {mode === "countdown" ? (
            <>
              <div className="text-sm font-bold text-slate-800">Set countdown</div>
              <div className="text-xs text-slate-500 mb-3">Adjust, then start when ready.</div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Minutes</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    className="w-full border-slate-300 rounded-lg shadow-sm focus:ring-orange-500 focus:border-orange-500 transition"
                    value={mm}
                    min={0}
                    max={999}
                    disabled={running}
                    onChange={(e) => setMm(Number(e.target.value))}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Seconds</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    className="w-full border-slate-300 rounded-lg shadow-sm focus:ring-orange-500 focus:border-orange-500 transition"
                    value={ss}
                    min={0}
                    max={59}
                    disabled={running}
                    onChange={(e) => setSs(Number(e.target.value))}
                  />
                </div>
              </div>

              <button
                type="button"
                className={cls(
                  "mt-3 w-full px-4 py-2.5 rounded-xl text-sm font-semibold border transition-colors",
                  running
                    ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
                )}
                onClick={() => applyCountdown(mm, ss)}
                disabled={running}
                title={running ? "Pause first to adjust" : "Apply time"}
              >
                Apply time
              </button>

              <div className="mt-3 grid grid-cols-2 gap-2">
                {[
                  { label: "1 min", m: 1, s: 0 },
                  { label: "2 min", m: 2, s: 0 },
                  { label: "3 min", m: 3, s: 0 },
                  { label: "5 min", m: 5, s: 0 },
                ].map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    className={cls(
                      "px-3 py-2 rounded-xl text-sm font-semibold border bg-white/70 border-slate-200 hover:bg-slate-100 transition-colors",
                      running && "opacity-50 cursor-not-allowed"
                    )}
                    onClick={() => !running && applyCountdown(p.m, p.s)}
                    disabled={running}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              <div className="mt-4 text-xs text-slate-500">
                Tip: Use short bursts (1–3 min) for tricky passages.
              </div>
            </>
          ) : (
            <>
              <div className="text-sm font-bold text-slate-800">Laps</div>
              <div className="text-xs text-slate-500 mb-3">Capture checkpoints while you practice.</div>

              {laps.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-white/60 p-3 text-sm text-slate-600">
                  Tap <span className="font-semibold">Lap</span> to record a checkpoint.
                </div>
              ) : (
                <div className="max-h-56 overflow-auto pr-1 space-y-2">
                  {laps.map((l, idx) => (
                    <div key={l.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white/60 px-3 py-2">
                      <div className="text-xs font-semibold text-slate-500">Lap {laps.length - idx}</div>
                      <div className="font-bold text-slate-800">{formatClock(l.t)}</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4 text-xs text-slate-500">
                Tip: Use laps for “clean run” attempts and track improvements.
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delete confirm */}
      {confirmOpen && (
        <ConfirmModal
          title="Delete timer?"
          message="Remove this timer tool from the note? This cannot be undone."
          confirmText="Delete"
          confirmButtonClass="bg-red-500 hover:bg-red-600"
          onConfirm={() => {
            setConfirmOpen(false);
            deleteNode?.();
          }}
          onClose={() => setConfirmOpen(false)}
        />
      )}
    </NodeViewWrapper>
  );
}

const TimerToolNode = Node.create({
  name: "timerTool",
  group: "block",
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      noteBlockId: { default: null },
      mode: { default: "timer" },
    };
  },
  parseHTML() {
    return [{ tag: "timer-tool" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["timer-tool", mergeAttributes(HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(TimerToolBlock);
  },
});

export default TimerToolNode;